import {
  Component,
  Inject,
  Injector,
  forwardRef,
  ElementRef,
  OnInit,
  Optional,
  NgModule,
  ViewEncapsulation,
  Renderer
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ValidatorFn } from '@angular/forms/src/directives/validators';
import { OSharedModule } from '../../../shared';
import { OFormComponent } from '../../form/o-form.component';
import { OFormValue } from '../../form/OFormValue';
import { InputConverter } from '../../../decorators';
import {
  OTextInputModule,
  OTextInputComponent,
  DEFAULT_INPUTS_O_TEXT_INPUT,
  DEFAULT_OUTPUTS_O_TEXT_INPUT
} from '../text-input/o-text-input.component';

import {
  OIntegerPipe,
  IIntegerPipeArgument
} from '../../../pipes';

export const DEFAULT_INPUTS_O_INTEGER_INPUT = [
  ...DEFAULT_INPUTS_O_TEXT_INPUT,
  'min',
  'max',
  'step',
  'grouping',
  'thousandSeparator : thousand-separator',
  'olocale : locale'
];

export const DEFAULT_OUTPUTS_O_INTEGER_INPUT = [
  ...DEFAULT_OUTPUTS_O_TEXT_INPUT
];



export class OIntegerInputComponent extends OTextInputComponent implements OnInit {

  public static DEFAULT_INPUTS_O_INTEGER_INPUT = DEFAULT_INPUTS_O_INTEGER_INPUT;
  public static DEFAULT_OUTPUTS_O_INTEGER_INPUT = DEFAULT_OUTPUTS_O_INTEGER_INPUT;

  @InputConverter()
  min: number;
  @InputConverter()
  max: number;
  @InputConverter()
  step: number;

  @InputConverter()
  protected grouping: boolean = false;
  protected thousandSeparator: string;
  protected olocale: string;

  protected componentPipe: OIntegerPipe;
  protected pipeArguments: IIntegerPipeArgument;
  protected focused: boolean = false;
  protected renderer: Renderer;
  protected inputWidth;
  protected avoidBlur: boolean = false;
  protected changeTimeout: any;

  constructor(
      form: OFormComponent,
    elRef: ElementRef,
    injector: Injector
  ) {
    super(form, elRef, injector);
    this.renderer = this.injector.get(Renderer);
    this.setComponentPipe();
  }

  setComponentPipe() {
    this.componentPipe = new OIntegerPipe(this.injector);
  }

  ngOnInit() {
    super.ngOnInit();

    this.pipeArguments = {
      grouping: this.grouping,
      thousandSeparator: this.thousandSeparator,
      locale: this.olocale
    };

    if (this.step === undefined) {
      this.step = 1;
    }
  }

  getControl(): FormControl {
    let control: FormControl = super.getControl();
    if (control) {
      control.statusChanges.debounceTime(100).subscribe(values => {
        if (!this.focused) {
          this.setPipeValue();
        }
      });
    }
    return control;
  }

  innerOnChange(event: any) {
    if (!this.value) {
      this.value = new OFormValue();
    }
    if (this.focused) {
      let parsedValue = this.componentPipe.transform(this.value.value, this.pipeArguments);
      this.value.value = parsedValue;
    }
    this.ensureOFormValue(event);
    this.onChange.emit(event);
    this.registerInactivityCallback();
  }

  registerInactivityCallback() {
    if (this.changeTimeout) {
      clearInterval(this.changeTimeout);
    }
    const self = this;
    function timerCallback() {
      self.avoidBlur = false;
      self.innerOnBlur();
    }
    this.changeTimeout = setTimeout(function () {
      timerCallback();
    }, 1000);
  }

  innerOnFocus(event: any) {
    event.stopPropagation();
    event.preventDefault();
    this.avoidBlur = true;
    this.focused = true;
    if (this.isReadOnly) {
      return;
    }
    this.setNumberDOMValue(this.getValue());
    if (!this.isReadOnly && !this.isDisabled) {
      this.onFocus.emit(event);
    }
  }

  innerOnBlur(event?: any) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.avoidBlur) {
      this.registerInactivityCallback();
      return;
    }
    this.focused = false;
    if (this.isReadOnly) {
      return;
    }
    this.setPipeValue();
    let formControl = this.getControl();
    if (formControl) {
      formControl.updateValueAndValidity();
    }
    if (!this.isReadOnly && !this.isDisabled) {
      this.onBlur.emit(event);
    }
  }

  setPipeValue() {
    if (typeof this.pipeArguments !== 'undefined' && !this.isEmpty()) {
      let parsedValue = this.componentPipe.transform(this.getValue(), this.pipeArguments);
      this.setTextDOMValue(parsedValue);
    }
  }

  isEmpty(): boolean {
    if (this.value instanceof OFormValue) {
      if (this.value.value !== undefined) {
        return false;
      }
    }
    return true;
  }

  getInputEl() {
    var inputElement = undefined;
    if (this.elRef.nativeElement.tagName === 'INPUT') {
      inputElement = this.elRef.nativeElement;
    } else {
      inputElement = this.elRef.nativeElement.getElementsByTagName('INPUT')[0];
    }
    return inputElement;
  }

  setNumberDOMValue(val: any) {
    const inputElement = this.getInputEl();
    if (inputElement !== undefined) {
      if (!this.inputWidth) {
        this.inputWidth = inputElement.parentElement.parentElement.clientWidth;
      }
      this.renderer.setElementStyle(this.elRef.nativeElement, 'width', (this.inputWidth + 'px'));
      inputElement.type = 'number';
      inputElement.value = (val !== undefined) ? val : '';
    }
  }

  setTextDOMValue(val: any) {
    const inputElement = this.getInputEl();
    if (inputElement !== undefined) {
      inputElement.type = 'text';
      inputElement.value = (val !== undefined) ? val : '';
    }
  }

  resolveValidators(): ValidatorFn[] {
    let validators: ValidatorFn[] = super.resolveValidators();
    if (typeof (this.min) !== 'undefined') {
      validators.push(this.minValidator.bind(this));
    }
    if (typeof (this.max) !== 'undefined') {
      validators.push(this.maxValidator.bind(this));
    }
    return validators;
  }

  protected minValidator(control: FormControl) {
    if ((typeof (control.value) === 'number') && (control.value < this.min)) {
      return {
        'min': {
          'requiredMin': this.min
        }
      };
    }
    return {};
  }

  protected maxValidator(control: FormControl) {
    if ((typeof (control.value) === 'number') && (this.max < control.value)) {
      return {
        'max': {
          'requiredMax': this.max
        }
      };
    }
    return {};
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'o-integer-input',
  template: `
    <div [formGroup]="getFormGroup()" [mdTooltip]="tooltip" [mdTooltipPosition]="tooltipPosition" [mdTooltipShowDelay]="tooltipShowDelay">
        <md-form-field #innerInputContainer fxFlexFill>
            <input mdInput type="number" [attr.id]="getAttribute()" [id]="getAttribute()" [formControlName]="getAttribute()"
                [placeholder]="placeHolder" [ngModel]="getValue()" (ngModelChange)="innerOnChange($event)" (focus)="innerOnFocus($event)"
                (blur)="innerOnBlur($event)" [readonly]="isReadOnly" [min]="min" [max]="max" [step]="step">
            <md-error *ngIf="hasError('required')">{{ 'FORM_VALIDATION.REQUIRED' | oTranslate }}</md-error>
            <md-error *ngIf="hasError('min')">{{ 'FORM_VALIDATION.MIN_VALUE' | oTranslate }}: {{ getErrorValue('min', 'requiredMin') }}</md-error>
            <md-error *ngIf="hasError('max')">{{ 'FORM_VALIDATION.MAX_VALUE' | oTranslate }}: {{ getErrorValue('max', 'requiredMax') }}</md-error>
        </md-form-field>
    </div>
  `,
  styles: [`

  `],
  inputs: [
    ...DEFAULT_INPUTS_O_INTEGER_INPUT
  ],
  outputs: [
    ...DEFAULT_OUTPUTS_O_INTEGER_INPUT
  ],
  encapsulation: ViewEncapsulation.None
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: OFormComponent, decorators: [{ type: Optional }, { type: Inject, args: [forwardRef(() => OFormComponent), ] }, ]},
{type: ElementRef, },
{type: Injector, },
];
}



export class OIntegerInputModule {
static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  declarations: [OIntegerInputComponent],
  imports: [OSharedModule, CommonModule, OTextInputModule],
  exports: [OIntegerInputComponent, OTextInputModule],
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
